/*********************************************************************************
 * Copyright (C) 2025 VECROS Technologies Pvt. Ltd.
 * <legal@vecros.com>
 * This file is part of VECROS CLOUD SOFTWARE SERVICES maintained and developed by VECROS TECHNOLOGIES PRIVATE LIMITED.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 **********************************************************************************/

import { useEffect, useRef, useState } from "react";
import { Alert, Box, Button, CircularProgress, Typography, useTheme } from "@mui/material";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getAuthBaseUrl, redirectToAccountsLogin } from "../../utils/authEnvironment";

const ATTEMPT_KEY = "vcs_drive_login_redirect_at";
// If we bounced to accounts this recently and are still anonymous, the handoff
// is broken rather than merely slow.
const LOOP_WINDOW_MS = 15000;

/**
 * Drive has no login form. Identity lives in the accounts app, which signs the
 * user in and mints the vecros-api session cookie this app runs on; this route
 * exists only to bounce the browser there and to catch stale /auth/login links.
 */
const Login = () => {
  const theme = useTheme();
  const location = useLocation();
  const { isAuthenticated, isChecking } = useAuth();
  const redirected = useRef(false);
  const [loopDetected, setLoopDetected] = useState(false);

  // Where the user was originally headed, stashed by ProtectedRoute.
  const returnTo = location.state?.from
    ? `${location.state.from.pathname}${location.state.from.search || ""}`
    : "/";

  useEffect(() => {
    // Wait for the session probe: a user arriving back from accounts already
    // has a cookie, and bouncing them again would loop.
    if (isChecking || isAuthenticated || redirected.current) return;

    // Guard the handoff. Coming back from accounts still-anonymous means the
    // session cookie never arrived — most often because accounts and drive are
    // pointed at different vecros-api origins, so the cookie was set on a
    // domain this app cannot read. Bouncing again would spin forever.
    const lastAttempt = Number(sessionStorage.getItem(ATTEMPT_KEY) || 0);
    if (lastAttempt && Date.now() - lastAttempt < LOOP_WINDOW_MS) {
      sessionStorage.removeItem(ATTEMPT_KEY);
      setLoopDetected(true);
      return;
    }

    redirected.current = true;
    sessionStorage.setItem(ATTEMPT_KEY, String(Date.now()));
    redirectToAccountsLogin(returnTo);
  }, [isChecking, isAuthenticated, returnTo]);

  // A good handoff clears the marker so the next expiry redirects normally.
  useEffect(() => {
    if (isAuthenticated) sessionStorage.removeItem(ATTEMPT_KEY);
  }, [isAuthenticated]);

  if (isAuthenticated) return <Navigate to={returnTo} replace />;

  const retry = () => {
    setLoopDetected(false);
    redirected.current = true;
    sessionStorage.setItem(ATTEMPT_KEY, String(Date.now()));
    redirectToAccountsLogin(returnTo);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        px: 2,
        height: "100vh",
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.text.color,
      }}
    >
      {loopDetected ? (
        <>
          <Alert severity="warning" sx={{ maxWidth: 560 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 0.5 }}>
              Signed in at Accounts, but Drive still can't see your session.
            </Typography>
            <Typography sx={{ fontSize: 13 }}>
              This usually means Accounts and Drive are configured against different
              API origins, so the session cookie was set on a domain Drive cannot
              read. Check that <code>REACT_APP_API_BASE_URL</code> matches in both apps.
            </Typography>
          </Alert>
          <Button onClick={retry} sx={{ color: "accent.main", textTransform: "none" }}>
            Try signing in again
          </Button>
        </>
      ) : (
        <>
          <CircularProgress sx={{ color: "accent.main" }} />
          <Typography sx={{ fontSize: 15 }}>Redirecting you to Vecros Accounts…</Typography>
          <Button onClick={retry} sx={{ color: "accent.main", textTransform: "none" }}>
            Continue to {getAuthBaseUrl().replace(/^https?:\/\//, "")}
          </Button>
        </>
      )}
    </Box>
  );
};

export default Login;
